import { useEffect, useState } from 'react';
import { DiscardChangesDialog } from '@/components/custom/discard-changes-dialog';

interface Options {
  /** The sheet's open state — used to clear a stale prompt when it closes. */
  open: boolean;
  /** The parent's open-state setter — only called once a close is allowed. */
  onOpenChange: (open: boolean) => void;
  /** Does the form hold edits the user would lose on close? For react-hook-form
   *  pass `form.formState.isDirty`; for useState forms compare to the seeded
   *  baseline. */
  isDirty: boolean;
  /** Block close entirely while a save is in flight. */
  isSubmitting?: boolean;
  /** Override the discard prompt copy. */
  title?: string;
  description?: string;
}

interface Guard {
  /**
   * Drop-in replacement for the raw `onOpenChange`. Wire it to both
   * `<Sheet onOpenChange>` (covers ESC, overlay click, the X button) and any
   * Cancel button (`onClick={() => requestOpenChange(false)}`). Opening passes
   * straight through; a close attempt on a dirty form opens the prompt instead.
   */
  requestOpenChange: (next: boolean) => void;
  /** Render once inside the component — the discard confirmation dialog. */
  discardDialog: React.ReactElement;
}

/**
 * Guards a form sheet/sidebar against losing unsaved edits. Every close path —
 * ESC, overlay click, the X, Cancel — funnels through `requestOpenChange`,
 * which prompts before discarding when the form is dirty and refuses to close
 * mid-save. The prompt is cleared whenever the sheet closes so a stale
 * confirmation can't resurface on the next open.
 *
 * Note: these sheets are modal (Radix Dialog), so the overlay makes the page
 * behind them inert — a click "on another row" is intercepted by the overlay
 * and handled as a close attempt, which this guard catches. There's therefore
 * no separate in-place target-switch path to guard.
 */
export function useUnsavedChangesGuard({
  open,
  onOpenChange,
  isDirty,
  isSubmitting = false,
  title,
  description,
}: Options): Guard {
  const [showDiscard, setShowDiscard] = useState(false);

  // Clear a pending prompt if the sheet closes by any other means (e.g. a
  // programmatic close from the parent) so it can't reappear on the next open.
  useEffect(() => {
    if (!open) setShowDiscard(false);
  }, [open]);

  const requestOpenChange = (next: boolean): void => {
    if (next) {
      onOpenChange(true);
      return;
    }
    if (isSubmitting) return; // never abandon a save in flight
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    onOpenChange(false);
  };

  const discardDialog = (
    <DiscardChangesDialog
      open={showDiscard}
      title={title}
      description={description}
      onKeepEditing={() => setShowDiscard(false)}
      onDiscard={() => {
        setShowDiscard(false);
        onOpenChange(false);
      }}
    />
  );

  return { requestOpenChange, discardDialog };
}
